// useSFUCall.js — SFU-based call hook (replaces P2P mesh in useCall.js)
//
// Architecture: each participant has a single RTCPeerConnection with the
// SFU running on the server (internal/sfu). The client publishes its
// local tracks (mic, camera, screen) to the SFU and subscribes to all
// other participants' tracks via the same PeerConnection. When a new
// participant joins or publishes a track, the SFU sends a renegotiation
// offer to existing participants with the new tracks added.
//
// This hook exposes the same public interface as useCall.js so
// CallView.jsx and GroupChannelView.jsx don't need changes.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { IncomingCall, PublicUser } from '../types'
import { API_URL } from '../api'
import { getToken } from '../api'
import { createSFUNegotiation } from '../utils/sfu-negotiation'

const WS_URL = (import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || 'ws://localhost:3000').replace(/^http/, 'ws')

// Browser-native AEC/NS/AGC. Always request these — they run before
// our own high-pass + optional RNNoise and are the biggest win vs a
// raw `audio: true` capture.
function audioConstraints(deviceId?: string) {
  const c: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  }
  // Empty / "default" = follow the OS default. Chrome treats
  // deviceId:"default" as a live alias that hops to the new
  // headset when it becomes the system default. Prefer it
  // explicitly — a bare `audio: true` often binds to the first
  // enumerated device (the notebook mic), not the comms default.
  if (deviceId && deviceId !== 'default') c.deviceId = { exact: deviceId }
  else c.deviceId = { ideal: 'default' }
  return c
}

// Audio without a pinned deviceId. Used when the selected / OS-default
// device was unplugged — `ideal: "default"` still throws NotFoundError
// on Chrome if the default alias now points at a missing device.
function unconstrainedAudioConstraints() {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  }
}

// Progressive audio-only capture. Tries preferred device, then any
// mic, then the first enumerated audioinput. Throws only if every
// attempt fails (no mic left at all).
async function getAnyAudioStream(preferredDeviceId: string) {
  const attempts: Array<() => Promise<MediaStream>> = []
  if (preferredDeviceId) {
    attempts.push(() => navigator.mediaDevices.getUserMedia({
      audio: audioConstraints(preferredDeviceId), video: false,
    }))
  }
  attempts.push(() => navigator.mediaDevices.getUserMedia({
    audio: audioConstraints(), video: false,
  }))
  attempts.push(() => navigator.mediaDevices.getUserMedia({
    audio: unconstrainedAudioConstraints(), video: false,
  }))
  attempts.push(() => navigator.mediaDevices.getUserMedia({
    audio: true, video: false,
  }))
  attempts.push(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const first = devices.find(d =>
      d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications'
    ) || devices.find(d => d.kind === 'audioinput' && d.deviceId)
    if (!first) throw new DOMException('Requested device not found', 'NotFoundError')
    return navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: first.deviceId } }, video: false,
    })
  })
  let lastErr
  for (const attempt of attempts) {
    try {
      const stream = await attempt()
      if (stream?.getAudioTracks().length) return stream
    } catch (err: unknown) {
      lastErr = err
      console.warn('📞 SFU getAnyAudioStream: attempt failed', (err as Error).name, (err as Error).message)
    }
  }
  throw lastErr || new DOMException('Requested device not found', 'NotFoundError')
}

// Raise the Opus send bitrate above Chrome's ~32kbps voice default so
// speech encodes fullband instead of narrowband-under-pressure, and mark
// the encoding high-priority so audio wins when bandwidth estimation
// starts dropping packets (networkPriority maps to DSCP EF on Chrome).
function tuneAudioSender(sender: RTCRtpSender | null) {
  if (!sender) return
  try {
    const params = sender.getParameters()
    if (!params.encodings?.length) params.encodings = [{}]
    for (const enc of params.encodings) {
      enc.maxBitrate = 64_000
      enc.priority = 'high'
      enc.networkPriority = 'high'
    }
    sender.setParameters(params).catch((err: unknown) => {
      console.warn('📞 SFU tuneAudioSender setParameters failed:', err)
    })
  } catch (err: unknown) {
    console.warn('📞 SFU tuneAudioSender failed:', err)
  }
}

// Enlarge the jitter buffer on a remote audio receiver. Chrome's default
// NetEq target is adaptive but starts near zero, so bursty jitter (WiFi,
// TURN-TCP) produces audible dropouts before it adapts. A fixed ~150ms
// target trades imperceptible voice latency for a large dropout cushion.
function tuneAudioReceiver(receiver: RTCRtpReceiver | null | undefined) {
  if (!receiver) return
  try {
    ;(receiver as RTCRtpReceiver & { jitterBufferTarget?: number }).jitterBufferTarget = 150
  } catch (err: unknown) {
    console.warn('📞 SFU jitterBufferTarget failed:', err)
  }
}

async function getMicPermissionState() {
  if (!navigator.permissions?.query) return 'unknown'
  try {
    const status = await navigator.permissions.query({ name: 'microphone' })
    return status.state // 'granted' | 'denied' | 'prompt'
  } catch {
    return 'unknown'
  }
}

function audioErrorMessage(err: { name?: string; message?: string }, permState?: string) {
  if (permState === 'denied' || err?.name === 'NotAllowedError') {
    return 'Microfone bloqueado neste site. Clique no cadeado ao lado da URL → Microfone → Permitir, depois recarregue a página.'
  }
  if (err?.name === 'NotFoundError') {
    return 'Nenhum microfone encontrado. Confira o dispositivo em Preferências do Sistema e se o Chrome está com Microfone permitido.'
  }
  if (err?.name === 'NotReadableError') {
    return `Microfone em uso por outro app. O outro usuário não consegue te ouvir. (${(err as Error).message})`
  }
  return `Microfone indisponível: ${err?.name || 'erro'} — ${err?.message || ''}. O outro usuário não consegue te ouvir.`
}

function pickDefaultId(list: MediaDeviceInfo[]) {
  if (!list.length) return ''
  return list.find(d => d.deviceId === 'default')?.deviceId
    || list.find(d => /default/i.test(d.label))?.deviceId
    || list[0].deviceId
}

function normalizeDeviceId(id: string | undefined, list: MediaDeviceInfo[]) {
  if (!id) return pickDefaultId(list)
  return list.some(d => d.deviceId === id) ? id : pickDefaultId(list)
}

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'turn:80.241.222.172:3478', username: 'atlas-turn', credential: 'K9m2xR7vQ4wL' },
  { urls: 'turn:80.241.222.172:3478?transport=tcp', username: 'atlas-turn', credential: 'K9m2xR7vQ4wL' },
]

export function useSFUCall() {
  // --- Public state (mirrors useCall.js interface) ---
  const [callState, setCallState] = useState('idle') // idle | calling | connected | failed
  const [callId, setCallId] = useState<string | null>(null)
  const [channelId, setChannelId] = useState<string | null>(null)
  const [isChannelCall, setIsChannelCall] = useState(false)
  const [isVideo, setIsVideo] = useState(false)
  const [muted, setMuted] = useState(false)
  const [videoOff, setVideoOff] = useState(false)
  const [screenSharing, setScreenSharing] = useState(false)
  const [error, setError] = useState('')
  const [reconnecting, setReconnecting] = useState(false)
  const [remoteStreams, setRemoteStreams] = useState<Record<string, MediaStream>>({}) // userId -> MediaStream
  // participants tracks all user IDs in the current call, including those
  // who haven't published tracks yet. Populated by sfu-participant-joined
  // and ontrack, cleared on sfu-participant-left and call cleanup.
  const [participants, setParticipants] = useState<string[]>([])
  const participantsRef = useRef<Record<string, boolean>>({})
  const [callTargetId, setCallTargetId] = useState<string | null>(null)
  const callTargetIdRef = useRef<string | null>(null)
  // Remote mute states: userId -> boolean (true = muted)
  const [remoteMuteStates, setRemoteMuteStates] = useState<Record<string, boolean>>({})
  // Remote speaking states: userId -> boolean (true = speaking)
  const [remoteSpeaking, setRemoteSpeaking] = useState<Record<string, boolean>>({})
  // Inviting users: userId -> 'calling' | 'rejected' | 'joined' | 'timeout'
  const [invitingUsers, setInvitingUsers] = useState<Record<string, string>>({})
  const remoteSpeakingRef = useRef<Record<string, boolean>>({})
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null)
  const incomingCallRef = useRef<IncomingCall | null>(null)
  const [selectedDevices, setSelectedDevices] = useState({ audioIn: '', audioOut: '', videoIn: '' })
  const [outputVolume, setOutputVolume] = useState(1)
  // devices holds the enumerated MediaDevicesInfo arrays, grouped by
  // kind, so the settings UI can render <option> lists. Populated by
  // loadDevices().
  const [devices, setDevices] = useState<{ audioIn: MediaDeviceInfo[]; audioOut: MediaDeviceInfo[]; videoIn: MediaDeviceInfo[] }>({ audioIn: [], audioOut: [], videoIn: [] })
  const [inputVolume, setInputVolume] = useState(1)
  const inputVolumeRef = useRef(1)
  const rawAudioTrackRef = useRef<MediaStreamTrack | null>(null)
  // localStream state triggers re-render so the useEffect that attaches
  // it to the <video> element runs after the element is mounted.
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  // micError is set when getUserMedia fails — the UI shows a warning
  // so the user knows the other person can't hear them. Cleared when
  // getUserMedia succeeds (e.g. after retrying with default device).
  const [micError, setMicError] = useState('')
  const voiceDetectRef = useRef<{ ctx: AudioContext; analyser: AnalyserNode; rafId: number; lastSent?: number } | null>(null) // { ctx, analyser, rafId, lastSent }

  // --- Refs (mutable, don't trigger re-render) ---
  const wsRef = useRef<WebSocket | null>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  // localVideoRef is attached to the <video> element that renders the
  // local camera preview. CallView uses it via the hook's return value.
  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const screenStreamRef = useRef<MediaStream | null>(null)
  const originalVideoTrackRef = useRef<MediaStreamTrack | null>(null)
  // screenShareSenderRef holds the RTPSender for the screen-share track
  // so we can removeTrack it when sharing stops. Screen share is added
  // as a NEW m-line (addTransceiver), not by replacing the camera track.
  const screenShareSenderRef = useRef<RTCRtpSender | null>(null)
  const callIdRef = useRef<string | null>(null)
  const channelIdRef = useRef<string | null>(null)
  const isChannelCallRef = useRef(false)
  const isVideoRef = useRef(false)
  const callStateRef = useRef('idle')
  const mutedRef = useRef(false)
  const videoOffRef = useRef(false)
  // When true, the next local stream acquired will start with mic and
  // camera disabled. Used for forced joins (privacy: the person didn't
  // choose to enter the call, so we don't broadcast their mic/camera
  // until they explicitly toggle them on).
  const startMutedRef = useRef(false)
  // When true, the next local stream acquired will start with the camera
  // track disabled (but still present so the user can toggle it on).
  // Used by auto-rejoin to restore the user's camera-off state from
  // before the page reload. Separate from startMutedRef so that mic and
  // camera can be restored independently.
  const startVideoOffRef = useRef(false)
  const screenSharingRef = useRef(false)
  // Ringtone state (Web Audio API). Plays a gentle chime while the
  // caller waits for the callee to answer, or while the callee sees the
  // incoming-call screen.
  const ringtoneRef = useRef<{ ctx: AudioContext; interval: ReturnType<typeof setInterval>; vibrateInterval: ReturnType<typeof setInterval> | null } | null>(null)
  const ringtoneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const userIdRef = useRef<string | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  // Map: trackId -> { userId, kind, streamId } so we know who a remote
  // track belongs to when it arrives via OnTrack on the client side.
  // The SFU names forwarded tracks "sfu-<originalTrackId>" and keeps the
  // original stream ID, so we can identify the source user by stream ID
  // (which the publishing client sets to its user ID).
  const remoteTrackInfoRef = useRef<Record<string, unknown>>({})
  // Map: userId -> MediaStream (kept in sync with remoteStreams state)
  const remoteStreamsRef = useRef<Record<string, MediaStream>>({})
  const negotiationRef = useRef<ReturnType<typeof createSFUNegotiation> | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  // selectedDevicesRef mirrors selectedDevices state so async callbacks
  // (handleSignalingMessage, sendOfferWithLocalTracks) always see the
  // latest user-chosen devices instead of a stale closure value.
  const selectedDevicesRef = useRef({ audioIn: '', audioOut: '', videoIn: '' })
  // sendOfferRef and joinChannelCallRef break the circular dependency
  // between handleSignalingMessage (defined first, referenced in WS
  // onmessage) and sendOfferWithLocalTracks / joinChannelCall (defined
  // later but called from handleSignalingMessage). Without these refs,
  // the useCallback for handleSignalingMessage would capture a stale
  // (or TDZ) reference to those functions.
  const sendOfferRef = useRef<(() => unknown) | null>(null)
  const joinChannelCallRef = useRef<((callId: string, video?: boolean, chId?: string | null, forceMuted?: boolean, initialVideoOff?: boolean | null, isRejoin?: boolean, dmTargetUserId?: string | null) => unknown) | null>(null)
  const recaptureLocalMediaRef = useRef<(() => unknown) | null>(null)
  // Guards recaptureLocalMedia against concurrent calls (devicechange
  // fires multiple times when a headset with mic+speaker is unplugged).
  const recapturingRef = useRef(false)
  // Debounce timer for handleDeviceChange so we don't run loadDevices +
  // recapture multiple times when devicechange fires in bursts.
  const deviceChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ---------- Helpers ----------
  const getCurrentUserId = () => {
    const s = localStorage.getItem('atlas_user')
    if (s) { try { return JSON.parse(s).id } catch { return null } }
    return null
  }

  const setCallStateBoth = (s: 'idle' | 'calling' | 'connected' | 'failed') => {
    callStateRef.current = s
    setCallState(s)
  }
  // Keep incomingCallRef in sync so event handlers (notification WS)
  // can check the current incomingCall without capturing stale state.
  useEffect(() => { incomingCallRef.current = incomingCall }, [incomingCall])

  // ---------- WebSocket signaling ----------
  const connectWS = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    // Close any existing WS only if it's in CLOSING or CLOSED state.
    // If it's CONNECTING, give it time to establish — closing it prematurely
    // causes "WebSocket is closed before the connection is established".
    const old = wsRef.current
    if (old && (old.readyState === WebSocket.CLOSING || old.readyState === WebSocket.CLOSED)) {
      try { old.onclose = null; old.close() } catch {}
      wsRef.current = null
    }
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) return

    const token = getToken()
    if (!token) return
    const ws = new WebSocket(`${WS_URL}/ws/call?token=${encodeURIComponent(token)}`)
    wsRef.current = ws

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        handleSignalingMessage(msg)
      } catch (err: unknown) {
        console.error('📞 SFU WS parse error:', err)
      }
    }

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0
      setReconnecting(false)
      try { ws.send(JSON.stringify({ type: 'ping' })) } catch {}
      // If we're in an active call, rejoin the SFU room. 'calling'
      // counts too — a WS drop mid-dial otherwise leaves the new socket
      // unregistered, and the server drops every later sfu-offer/answer
      // as "inactive socket" while the UI sits on "Conectando...".
      if (callIdRef.current && callStateRef.current !== 'idle' && callStateRef.current !== 'failed') {
        rejoinRoom()
      }
    }

    ws.onclose = () => {
      if (callStateRef.current === 'connected' || callStateRef.current === 'calling') {
        setReconnecting(true)
      }
      const attempts = reconnectAttemptsRef.current++
      const delay = Math.min(1000 * Math.pow(2, attempts), 30000)
      console.log(`📞 SFU WS closed, reconnecting in ${delay}ms (attempt ${attempts + 1})`)
      reconnectTimerRef.current = setTimeout(() => connectWS(), delay)
    }

    ws.onerror = () => { /* onclose handles reconnect */ }
  }, [])

  // Rejoin the SFU room after a WS reconnect. We send sfu-join again so
  // the server re-creates our participant and we get a fresh PC. The old
  // PC is still alive but the server side was torn down when the WS
  // dropped, so we need a new one.
  const rejoinRoom = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    if (!callIdRef.current) return
    // Close the old PC — we'll create a new one when the server acks.
    if (pcRef.current) {
      try { pcRef.current.close() } catch {}
      pcRef.current = null
    }
    negotiationRef.current = null
    remoteStreamsRef.current = {}
    setRemoteStreams({})
    participantsRef.current = {}
    setParticipants([])
    wsRef.current.send(JSON.stringify({
      type: 'sfu-join',
      call_id: callIdRef.current,
      channel_id: channelIdRef.current || undefined,
      is_video: isVideoRef.current,
      rejoin: true,
    }))
  }, [])

  // ---------- Send helper ----------
  const send = (msg: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify(msg)) } catch (err: unknown) {
        console.error('📞 SFU send failed:', err)
      }
    }
  }

  // ---------- WS wait helper ----------
  // Returns true if WS is open, false if timed out. Callers should
  // check the return value and abort if false — sending on a closed
  // WS silently drops the message.
  const waitWsOpen = (timeoutMs = 8000) => new Promise(resolve => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return resolve(true)
    // Don't just wait: if the socket is dead the reconnect backoff can
    // take up to 30s, so kick connectWS now — otherwise accepting an
    // incoming call during a flap fails by timeout before ever dialing.
    connectWS()
    const check = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        clearInterval(check)
        clearTimeout(timer)
        resolve(true)
      }
    }, 100)
    const timer = setTimeout(() => {
      clearInterval(check)
      resolve(false)
    }, timeoutMs)
  })

  // ---------- ICE gathering helper ----------
  // Waits for ICE gathering to complete (or timeout). This ensures
  // the SDP we send includes inline ICE candidates, which is required
  // by some WebRTC implementations (including pion).
  const waitForIceGathering = (pc: RTCPeerConnection, timeoutMs = 3000) => new Promise<void>(resolve => {
    if (pc.iceGatheringState === 'complete') return resolve()
    const timer = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', check)
      resolve()
    }, timeoutMs)
    const check = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timer)
        pc.removeEventListener('icegatheringstatechange', check)
        resolve()
      }
    }
    pc.addEventListener('icegatheringstatechange', check)
  })

  // ---------- PeerConnection management ----------
  const createPC = useCallback(() => {
    if (pcRef.current) {
      try { pcRef.current.close() } catch {}
    }
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
    pcRef.current = pc
    const callId = callIdRef.current
    negotiationRef.current = createSFUNegotiation(
      pc,
      (type, sdp) => send({ type, call_id: callId, sdp }),
      () => pcRef.current === pc && callIdRef.current === callId && pc.signalingState !== 'closed',
    )

    // Send our ICE candidates to the SFU.
    pc.onicecandidate = (e) => {
      if (pcRef.current !== pc) return
      if (e.candidate) {
        send({
          type: 'sfu-ice-candidate',
          call_id: callIdRef.current,
          candidate: JSON.stringify(e.candidate.toJSON()),
        })
      }
    }

    // When the SFU adds a track to our PC (i.e. another participant's
    // forwarded track), wire it into a MediaStream keyed by the source
    // user ID. The SFU sets the stream ID to the publishing user's ID
    // (because we set it on the publishing side).
    pc.ontrack = (e) => {
      if (pcRef.current !== pc) return
      console.log('📞 SFU ontrack:', e.track.kind, 'id=', e.track.id, 'stream=', e.streams.map(s => s.id), 'mid=', e.transceiver.mid)
      // The SFU creates one MediaStream per source user (stream ID =
      // source user ID). We use that to group tracks.
      const streamId = e.streams[0]?.id || 'unknown'
      const userId = streamId
      console.log('📞 SFU ontrack: assigning to user', userId)
      if (e.track.kind === 'audio') tuneAudioReceiver(e.receiver)

      // Ensure this user is in the participants list (in case
      // sfu-participant-joined wasn't received, e.g. older SFU).
      if (!participantsRef.current[userId]) {
        participantsRef.current[userId] = true
        setParticipants(prev => prev.includes(userId) ? prev : [...prev, userId])
      }

      // Add the track to the user's MediaStream. Drop ended tracks so a
      // leftover black camera doesn't stay as the first video track —
      // <video> only renders one video track (the first).
      const existing = remoteStreamsRef.current[userId]
      let stream
      if (existing) {
        existing.getTracks().filter(t => t.readyState === 'ended').forEach(t => {
          try { existing.removeTrack(t) } catch {}
        })
        if (!existing.getTracks().some(t => t.id === e.track.id)) {
          existing.addTrack(e.track)
        }
        // Prefer the newest live video track so a screen share isn't
        // hidden behind a previous camera track in the same stream.
        const audio = existing.getAudioTracks().filter(t => t.readyState === 'live')
        const video = existing.getVideoTracks().filter(t => t.readyState === 'live')
        const latestVideo = video.length ? [video[video.length - 1]] : []
        stream = new MediaStream([...audio, ...latestVideo])
        remoteStreamsRef.current[userId] = stream
      } else {
        stream = new MediaStream([e.track])
        remoteStreamsRef.current[userId] = stream
      }
      // Always create a new object reference to trigger re-render.
      setRemoteStreams(prev => ({ ...prev, [userId]: stream }))

      // When the track starts receiving data (unmute event), force
      // a re-attach of the stream to the video element. This fixes
      // the case where the video element was attached before any
      // data arrived and doesn't start playing.
      e.track.onunmute = () => {
        console.log('📞 SFU track unmuted:', e.track.kind, 'for user', userId)
        // Re-create the stream to force re-attach in CallView
        const cur = remoteStreamsRef.current[userId]
        if (cur) {
          const fresh = new MediaStream(cur.getTracks())
          remoteStreamsRef.current[userId] = fresh
          setRemoteStreams(prev => ({ ...prev, [userId]: fresh }))
        } else {
          setRemoteStreams(prev => ({ ...prev, [userId]: stream }))
        }
      }
    }

    // Connection state monitoring.
    pc.onconnectionstatechange = () => {
      // Ignore events from a stale PC that was replaced by createPC.
      if (pcRef.current !== pc) return
      console.log('📞 SFU PC state:', pc.connectionState)
      if (pc.connectionState === 'connected') {
        setReconnecting(false)
        if (callStateRef.current === 'calling') {
          setCallStateBoth('connected')
        }
      } else if (pc.connectionState === 'failed') {
        setError('Conexão WebRTC falhou')
        setCallStateBoth('failed')
      }
    }

    pc.oniceconnectionstatechange = () => {
      if (pcRef.current !== pc) return
      console.log('📞 SFU ICE state:', pc.iceConnectionState)
      if (pc.iceConnectionState === 'disconnected') {
        setReconnecting(true)
      } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setReconnecting(false)
      }
    }

    return pc
  }, [])

  // ---------- Signaling message handler ----------
  interface SfuSignal {
  type?: string
  call_id?: string
  sdp?: string
  ice_candidate?: string
  user_id?: string
  from_user?: string
  from_user_id?: string
  channel_id?: string
  muted?: boolean
  speaking?: boolean
  is_video?: boolean
  is_group?: boolean
  sender_name?: string
  sender_avatar?: string
  error?: string
  [key: string]: unknown
}

  // ---------- Signaling message handler ----------
  const handleSignalingMessage = useCallback((msg: SfuSignal) => {
    switch (msg.type) {
      case 'sfu-joined':
        // Server acked our sfu-join. Now we create a PC, add our local
        // tracks, and send an offer.
        console.log('📞 SFU joined room', msg.call_id, 'sendOfferRef=', !!sendOfferRef.current)
        if (!callIdRef.current || callStateRef.current === 'idle' || callStateRef.current === 'failed') break
        callIdRef.current = msg.call_id ?? null
        setCallId(msg.call_id ?? null)
        // Seed the participants list with everyone already in the room.
        // Without this we only learn about peers via ontrack, so a peer
        // who isn't publishing (mic failed, joined muted) is invisible
        // and the UI sits on "Conectando..." forever.
        const existingIds = msg.participant_ids
        if (Array.isArray(existingIds)) {
          const myId = userIdRef.current
          for (const pid of existingIds as string[]) {
            if (pid && pid !== myId && !participantsRef.current[pid]) {
              participantsRef.current[pid] = true
              setParticipants(prev => prev.includes(pid) ? prev : [...prev, pid])
            }
          }
        }
        if (sendOfferRef.current) {
          sendOfferRef.current()
        } else {
          console.error('📞 SFU sendOfferRef is null — sendOfferWithLocalTracks not yet defined')
        }
        break

      case 'sfu-answer':
        // Answer to our initial offer (when we joined and published).
        console.log('📞 SFU received answer')
        if (msg.call_id && msg.call_id !== callIdRef.current) break
        if (msg.sdp) negotiationRef.current?.answer(msg.sdp).catch(err => {
          console.error('SFU applyAnswer failed:', err)
        })
        break

      case 'sfu-offer':
        // The SFU is renegotiating — it added a new participant's track
        // to our PC (or removed one). We must set remote description
        // and answer. Never resurrect a finished call from a late offer.
        console.log('📞 SFU received renegotiation offer', msg.call_id)
        if (!callIdRef.current || (msg.call_id && msg.call_id !== callIdRef.current)) break
        if (msg.sdp) negotiationRef.current?.remoteOffer(msg.sdp).catch(err => {
          console.error('SFU applyOffer failed:', err)
        })
        break

      case 'sfu-ice-candidate':
        // ICE candidate from the SFU.
        if (msg.call_id && msg.call_id !== callIdRef.current) break
        if (msg.ice_candidate) {
          applyICECandidate(msg.ice_candidate)
        }
        break

      case 'sfu-participant-joined': {
        // A new participant joined the SFU room. Add them to the
        // participants list so the UI shows them immediately, even
        // before they publish tracks (e.g. while getUserMedia is
        // loading or failed). When they do publish, ontrack will
        // add their streams to remoteStreams.
        const joinedId = msg.user_id
        console.log('📞 SFU participant joined:', joinedId)
        // Someone actually entered our room — the outgoing ringtone has
        // served its purpose. acceptCall goes straight to sfu-join in the
        // SFU flow and never sends 'call-answer', so this is the only
        // reliable "callee picked up" signal the caller gets.
        if (joinedId && (!msg.call_id || !callIdRef.current || msg.call_id === callIdRef.current)) {
          stopRingtone()
        }
        if (joinedId && !participantsRef.current[joinedId]) {
          participantsRef.current[joinedId] = true
          setParticipants(prev => prev.includes(joinedId) ? prev : [...prev, joinedId])
          // Mark invited user as joined
          setInvitingUsers(prev => {
            if (prev[joinedId]) {
              const next = { ...prev, [joinedId]: 'joined' }
              return next
            }
            return prev
          })
        }
        break
      }

      case 'sfu-participant-left': {
        // A participant left the SFU room. Remove their streams and
        // remove them from the participants list. The actual track
        // removal happens via renegotiation (sfu-offer), but we
        // clear the UI immediately.
        const leftId = msg.user_id
        console.log('📞 SFU participant left:', leftId)
        if (leftId && participantsRef.current[leftId]) {
          delete participantsRef.current[leftId]
          setParticipants(prev => prev.filter(id => id !== leftId))
        }
        if (leftId && remoteStreamsRef.current[leftId]) {
          delete remoteStreamsRef.current[leftId]
          setRemoteStreams(prev => {
            const next = { ...prev }
            delete next[leftId]
            return next
          })
        }
        // Clean up mute/speaking state for the departed user
        if (leftId) {
          delete remoteSpeakingRef.current[leftId]
          setRemoteSpeaking(prev => { const n = { ...prev }; delete n[leftId]; return n })
          setRemoteMuteStates(prev => { const n = { ...prev }; delete n[leftId]; return n })
        }
        break
      }

      case 'sfu-mute-state': {
        // A participant's mute state changed. Update the remote mute
        // states map so the UI can show a mic-off indicator.
        const uid = msg.user_id
        const isMuted = !!msg.muted
        if (uid) {
          setRemoteMuteStates(prev => ({ ...prev, [uid]: isMuted }))
        }
        break
      }

      case 'sfu-voice-activity': {
        // A participant started or stopped speaking. Update the
        // speaking map so the UI can show a voice indicator.
        const uid = msg.user_id
        const isSpeaking = !!msg.speaking
        if (uid) {
          remoteSpeakingRef.current[uid] = isSpeaking
          setRemoteSpeaking(prev => ({ ...prev, [uid]: isSpeaking }))
        }
        break
      }

      case 'sfu-force-mute': {
        // Another participant muted us remotely. Mute our mic.
        console.log('📞 SFU force-mute by', msg.from_user)
        if (localStreamRef.current) {
          const audio = localStreamRef.current.getAudioTracks()[0]
          if (audio && audio.enabled) {
            audio.enabled = false
            mutedRef.current = true
            setMuted(true)
            // Broadcast our new mute state so everyone updates their UI
            if (callIdRef.current) {
              send({ type: 'sfu-mute-state', call_id: callIdRef.current, muted: true })
            }
          }
        }
        break
      }

      case 'sfu-force-unmute': {
        // Another participant unmuted us remotely. Unmute our mic.
        console.log('📞 SFU force-unmute by', msg.from_user)
        if (localStreamRef.current) {
          const audio = localStreamRef.current.getAudioTracks()[0]
          if (audio && !audio.enabled) {
            audio.enabled = true
            mutedRef.current = false
            setMuted(false)
            // Broadcast our new mute state so everyone updates their UI
            if (callIdRef.current) {
              send({ type: 'sfu-mute-state', call_id: callIdRef.current, muted: false })
            }
          }
        }
        break
      }

      case 'sfu-call-ended':
        // The call was ended for everyone (someone clicked "Finalizar
        // para todos"). Tear down our side immediately — but only if
        // the call_id matches our current call. Otherwise this is a
        // stale message from a previous call that arrived late (e.g.
        // due to WS reconnection), and acting on it would tear down
        // the new call we just joined.
        console.log('📞 SFU call ended by another participant', msg.call_id, 'current=', callIdRef.current)
        if (msg.call_id && callIdRef.current && msg.call_id !== callIdRef.current) {
          console.log('📞 SFU call-ended: ignoring stale message for old call', msg.call_id)
          break
        }
        cleanupCallRef.current?.()
        setCallStateBoth('idle')
        break

      case 'sfu-error':
        console.error('📞 SFU error:', msg.error)
        setError(msg.error === 'call_ended'
          ? 'A chamada já foi encerrada'
          : (msg.error || 'Erro na chamada'))
        setCallStateBoth('failed')
        // A rejected join must not survive reloads — otherwise the
        // persisted-call rejoin recreates a ghost room every refresh
        // and the call buttons stay dead.
        clearPersistedCall()
        break

      case 'channel-call-invite':
        // Someone invited us to a call — ring so we can accept.
        if (callStateRef.current === 'idle' || callStateRef.current === 'failed') {
          setIncomingCall({
            fromUserId: msg.from_user_id,
            callId: msg.call_id,
            sdp: null,
            isVideo: msg.is_video,
            isGroup: msg.is_group !== false, // default true for backwards compat
            isChannelInvite: true,
            channelId: msg.channel_id || undefined,
            callerName: msg.sender_name,
            callerAvatar: msg.sender_avatar,
            forced: false,
          })
          startRingtone(true)
        }
        break

      case 'sfu-forced-join':
        // Someone force-joined us to a channel call — join immediately
        // without ringing. The server told us which call_id and channel
        // to join.
        console.log('📞 SFU forced join by', msg.from_user_id, 'call=', msg.call_id)
        if (callStateRef.current === 'idle' || callStateRef.current === 'failed') {
          joinChannelCallRef.current?.(msg.call_id ?? '', msg.is_video ?? false, msg.channel_id, true)
        }
        break

      case 'pong':
        break

      case 'call-answered':
        // The person we invited just accepted (via the old P2P path).
        // Stop our outgoing ringtone — the SFU flow takes over from here.
        stopRingtone()
        break

      case 'call-rejected':
        // The person we invited rejected the call.
        // For channel calls, don't tear down the whole call — just mark
        // the invited user as rejected so the inviter can re-invite.
        if (msg.from_user_id) {
          const fromId = msg.from_user_id
          setInvitingUsers(prev => ({ ...prev, [fromId]: 'rejected' }))
        }
        // DM calls now support extra invitees, so a rejection only ends
        // the call when the original callee rejects and nobody else has
        // joined. A third-party invitee rejecting (or someone already in
        // the room) must not tear down a live call.
        const rejectorIsDMMate = !msg.from_user_id || msg.from_user_id === callTargetIdRef.current
        const anyoneInRoom = Object.keys(participantsRef.current).length > 0
        if (isChannelCallRef.current || !rejectorIsDMMate || anyoneInRoom) {
          stopRingtone()
        } else {
          stopRingtone()
          setError('Chamada recusada')
          cleanupCallRef.current?.()
          setCallStateBoth('idle')
        }
        break

      case 'call-ended': {
        // The other side hung up while we were still ringing. Clear the
        // incoming-call toast too if it refers to this call — the server
        // sends this when a caller gives up before we joined.
        stopRingtone()
        if (!msg.call_id || incomingCallRef.current?.callId === msg.call_id) {
          setIncomingCall(null)
        }
        // Server closed our DM room because the other participant left
        // (explicit leave or PC-fail eviction). Without this the last
        // person stays "in call" alone forever — stuck state, busy to
        // callers, and auto-rejoin resurrecting the dead room.
        if ((!msg.call_id || msg.call_id === callIdRef.current) &&
            (callStateRef.current === 'calling' || callStateRef.current === 'connected')) {
          setError('A outra pessoa encerrou a chamada')
          cleanupCallRef.current?.()
          setCallStateBoth('failed')
        }
        break
      }

      default:
        // Ignore P2P messages (call-offer, call-answer, etc.) — we're
        // using the SFU now.
        break
    }
    // NOTE: deps array is intentionally empty. sendOfferWithLocalTracks
    // and joinChannelCall are defined AFTER this useCallback in the
    // function body, so referencing them in the deps array would throw
    // a TDZ error ("Cannot access X before initialization"). We call
    // them via refs (sendOfferRef / joinChannelCallRef) which are
    // updated on every render after they're defined.
  }, [])

  // ---------- Local stream attachment ----------
  // This useEffect bridges the race condition between getLocalStream
  // (which acquires the camera/mic) and the <video> element mounting
  // (which happens when CallView re-renders with isVideo=true). If
  // getLocalStream runs before the video element is mounted, the ref
  // is null and srcObject can't be set. This effect runs after every
  // render where localStream or callState changed, so it catches the
  // case where the element becomes available later.
  useEffect(() => {
    console.log('📞 SFU localStream effect:', { localStream: !!localStream, videoEl: !!localVideoRef.current, callState })
    if (localStream && localVideoRef.current) {
      if (localVideoRef.current.srcObject !== localStream) {
        localVideoRef.current.srcObject = localStream
        localVideoRef.current.play().catch(() => {})
      }
    }
  }, [localStream, callState, isVideo])

  // ---------- ICE application ----------
  const applyICECandidate = async (candJSON: string) => {
    try {
      await negotiationRef.current?.candidate(JSON.parse(candJSON))
    } catch (err: unknown) {
      console.warn('📞 SFU addIceCandidate failed:', (err as Error).message)
    }
  }

  // ---------- Local tracks & offer ----------
  const getLocalStream = async (video: boolean) => {
    if (localStreamRef.current) return localStreamRef.current
    const dev = selectedDevicesRef.current
    console.log('📞 SFU getLocalStream: acquiring audio independently', { video, selectedDevices: { ...dev } })
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      console.log('📞 SFU getLocalStream: available devices', devices.map(d => ({ kind: d.kind, label: d.label, deviceId: d.deviceId.slice(0, 8) })))
    } catch (e: unknown) {
      console.warn('📞 SFU getLocalStream: enumerateDevices failed', e)
    }

    // Never request audio+video in the same getUserMedia. A missing
    // camera throws NotFoundError for the whole request and drops the
    // mic even when one is still plugged in. Same the other way: a
    // missing mic must not skip a working camera.
    let stream: MediaStream | null = null
    let audioErr: Error | null = null
    try {
      stream = await getAnyAudioStream(dev.audioIn)
      console.log('📞 SFU getLocalStream: got audio', stream!.id, 'tracks:', stream.getTracks().map(t => ({ kind: t.kind, label: t.label })))
    } catch (err: unknown) {
      audioErr = err as Error
      console.error('📞 SFU getLocalStream: all audio attempts failed', {
        name: (err as Error).name, message: (err as Error).message,
      })
      selectedDevicesRef.current = { ...selectedDevicesRef.current, audioIn: '' }
      setSelectedDevices(prev => ({ ...prev, audioIn: '' }))
    }

    if (video) {
      const videoConstraints = dev.videoIn
        ? { deviceId: { exact: dev.videoIn } }
        : { width: { ideal: 640 }, height: { ideal: 480 } }
      try {
        const cam = await navigator.mediaDevices.getUserMedia({ audio: false, video: videoConstraints })
        if (stream) {
          cam.getVideoTracks().forEach(t => stream!.addTrack(t))
        } else {
          stream = cam
        }
        console.log('📞 SFU getLocalStream: added camera track')
      } catch (err: unknown) {
        console.warn('📞 SFU getLocalStream: camera unavailable', (err as Error).name, (err as Error).message)
        if (dev.videoIn) {
          try {
            const cam = await navigator.mediaDevices.getUserMedia({
              audio: false, video: { width: { ideal: 640 }, height: { ideal: 480 } },
            })
            if (stream) cam.getVideoTracks().forEach(t => stream!.addTrack(t))
            else stream = cam
            selectedDevicesRef.current = { ...selectedDevicesRef.current, videoIn: '' }
            setSelectedDevices(prev => ({ ...prev, videoIn: '' }))
            console.log('📞 SFU getLocalStream: camera fallback without deviceId succeeded')
          } catch (err2: unknown) {
            console.warn('📞 SFU getLocalStream: camera fallback also failed', (err2 as Error).name, (err2 as Error).message)
            isVideoRef.current = false
            setIsVideo(false)
          }
        } else {
          isVideoRef.current = false
          setIsVideo(false)
        }
      }
    }

    // Always refresh the device list so settings is not empty after a
    // failed getUserMedia (Chrome hides labels until any GUM succeeds,
    // and even a camera-only GUM unlocks the full list).
    loadDevices()

    if (!stream) {
      try { localStorage.removeItem('atlas_selected_devices') } catch {}
      throw audioErr || new DOMException('Requested device not found', 'NotFoundError')
    }
    if (audioErr) {
      const perm = await getMicPermissionState()
      setMicError(audioErrorMessage(audioErr, perm))
    } else {
      setMicError('')
    }
    rawAudioTrackRef.current = stream.getAudioTracks()[0] || null
    // 'speech' tells the Opus encoder to optimize for voice (SILK/CELT
    // mode selection) rather than generic audio — improves clarity at
    // any given bitrate.
    try { if (rawAudioTrackRef.current) rawAudioTrackRef.current.contentHint = 'speech' } catch {}
    localStreamRef.current = stream
    setLocalStream(stream)
    // Start local voice activity detection
    startVoiceDetection(stream)
    // Attach the local stream to the <video> preview element if it
    // exists. CallView renders <video ref={localVideoRef}> and expects
    // the hook to populate srcObject. If the element isn't mounted yet
    // (race condition during call setup), the useEffect below will
    // attach it when it becomes available.
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream
    }
    return stream
  }

  // Do not route the published mic through AudioWorklet /
  // MediaStreamDestination. That tears the track out of the browser's
  // AEC path, so the other person hears their own voice as a hiss /
  // buzz. Native getUserMedia NS+AEC+AGC is enough and stays on the
  // capture graph. inputVolume stays as a UI preference for now.

  // ---------- Ringtone (Web Audio API) ----------
  // Gentle chime that repeats every 3s. Incoming calls use a C-major
  // arpeggio (C5→E5→G5); outgoing calls use a two-note motif (A4→D5).
  const startRingtone = useCallback((isIncoming = false) => {
    if (ringtoneRef.current) return // already playing
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (ctx.state === 'suspended') ctx.resume().catch(() => {})
      const playChime = () => {
        const now = ctx.currentTime
        const notes = isIncoming
          ? [{ f: 627.90, t: 0 }, { f: 791.10, t: 0.08 }, { f: 940.79, t: 0.16 }]
          : [{ f: 528.00, t: 0 }, { f: 704.80, t: 0.10 }]
        const volume = isIncoming ? 0.192 : 0.12
        notes.forEach(({ f, t }) => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.frequency.value = f
          osc.type = 'sine'
          const start = now + t
          const dur = 0.3
          gain.gain.setValueAtTime(0, start)
          gain.gain.linearRampToValueAtTime(volume, start + 0.01)
          gain.gain.exponentialRampToValueAtTime(0.001, start + dur)
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.start(start)
          osc.stop(start + dur)
        })
      }
      playChime()
      const interval = setInterval(playChime, 2000)
      let vibrateInterval: ReturnType<typeof setInterval> | null = null
      if (isIncoming && navigator.vibrate) {
        navigator.vibrate([400, 200, 400])
        vibrateInterval = setInterval(() => navigator.vibrate([400, 200, 400]), 2000)
      }
      ringtoneRef.current = { ctx, interval, vibrateInterval }
    } catch (err: unknown) {
      // AudioContext might not be available (e.g. older browser)
    }
  }, [])

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      clearInterval(ringtoneRef.current.interval)
      if (ringtoneRef.current.vibrateInterval) clearInterval(ringtoneRef.current.vibrateInterval)
      if (navigator.vibrate) navigator.vibrate(0)
      try { ringtoneRef.current.ctx.close() } catch {}
      ringtoneRef.current = null
    }
    if (ringtoneTimeoutRef.current) {
      clearTimeout(ringtoneTimeoutRef.current)
      ringtoneTimeoutRef.current = null
    }
  }, [])

  const sendOfferWithLocalTracks = useCallback(async () => {
    let pc: RTCPeerConnection | null = null
    try {
      pc = createPC()
      const negotiation = negotiationRef.current
      // Try to get local media (mic/camera). If it fails (permission
      // denied, no device, etc.), still send an offer so the PC
      // connects and we can receive remote tracks.
      let stream: MediaStream | null = null
      try {
        stream = await getLocalStream(isVideoRef.current)
        if (pcRef.current !== pc) return
        console.log('📞 SFU got local stream:', stream.id, 'tracks:', stream.getTracks().map(t => `${t.kind}:${t.enabled}`))
        for (const track of stream.getTracks()) {
          const sender = pc.addTrack(track, stream)
          if (track.kind === 'audio') tuneAudioSender(sender)
        }
        // For voice calls, disable video tracks.
        if (!isVideoRef.current) {
          stream.getVideoTracks().forEach(t => { t.enabled = false })
        }
        // Force-joined: start muted with camera off for privacy. The
        // user didn't choose to enter the call, so we don't broadcast
        // their mic/camera until they explicitly toggle them on.
        if (startMutedRef.current) {
          stream.getAudioTracks().forEach(t => { t.enabled = false })
          stream.getVideoTracks().forEach(t => { t.enabled = false })
          mutedRef.current = true
          setMuted(true)
          videoOffRef.current = true
          setVideoOff(true)
          console.log('📞 SFU force-join: starting muted with camera off')
        } else if (startVideoOffRef.current && isVideoRef.current) {
          // Auto-rejoin: the user had the camera off before the reload.
          // Acquire the camera track (so toggleVideo can enable it later)
          // but start with it disabled so we don't broadcast video the
          // user didn't intend to share.
          stream.getVideoTracks().forEach(t => { t.enabled = false })
          videoOffRef.current = true
          setVideoOff(true)
          console.log('📞 SFU auto-rejoin: starting with camera off (restored from persisted state)')
        }
      } catch (mediaErr: unknown) {
        if (pcRef.current !== pc) return
        const mErr = mediaErr as Error & { constraint?: string }
        console.error('📞 SFU getLocalStream FAILED in sendOffer, sending recvonly offer:', {
          name: mErr.name,
          message: mErr.message,
          constraint: mErr.constraint,
          stack: mErr.stack?.split('\n').slice(0, 5).join('\n'),
        })
        // Set micError so the UI shows a warning — the user needs to
        // know the other person can't hear them. Don't set callState
        // to 'failed' because we can still receive remote audio/video.
        const perm = await getMicPermissionState()
        setMicError(audioErrorMessage(mErr, perm))
        // Add recvonly transceivers so the offer includes media lines.
        // Without this, createOffer generates an SDP with no m= lines
        // (105 bytes), which the SFU rejects ("no ice-ufrag").
        pc.addTransceiver('audio', { direction: 'recvonly' })
        if (isVideoRef.current) {
          pc.addTransceiver('video', { direction: 'recvonly' })
        }
      }
      await negotiation?.offer()
    } catch (err: unknown) {
      if (pc && pcRef.current !== pc) return
      console.error('📞 SFU sendOffer failed:', err)
      setError('Erro ao iniciar: ' + (err as Error).message)
      setCallStateBoth('failed')
    }
  }, [createPC, getLocalStream])
  // Keep the ref in sync so handleSignalingMessage (defined earlier)
  // can call the latest version without capturing a stale closure.
  sendOfferRef.current = sendOfferWithLocalTracks

  // ---------- Call persistence for auto-rejoin on page reload ----------
  const PERSIST_KEY = 'atlas_active_call'

  const persistCall = useCallback((data: Record<string, unknown>) => {
    try { localStorage.setItem(PERSIST_KEY, JSON.stringify(data)) } catch {}
  }, [])

  // Merge partial updates into the persisted call so state changes
  // during a call (e.g. toggling the camera on) survive a page reload.
  // Without this, auto-rejoin reads the stale initial isVideo and
  // rejoins as audio-only even though the user had the camera on.
  const updatePersistedCall = useCallback((patch: Record<string, unknown>) => {
    try {
      const raw = localStorage.getItem(PERSIST_KEY)
      if (!raw) return
      const prev = JSON.parse(raw)
      localStorage.setItem(PERSIST_KEY, JSON.stringify({ ...prev, ...patch }))
    } catch {}
  }, [])

  const clearPersistedCall = useCallback(() => {
    try { localStorage.removeItem(PERSIST_KEY) } catch {}
  }, [])

  // ---------- Public actions ----------
  // startCall initiates a 1:1 DM call via the SFU. It creates a room,
  // joins it, and sends a call-invite to the target user so they hear
  // a ringtone and can accept/reject.
  const startCall = useCallback(async (targetUserId: string, video = false) => {
    if (!targetUserId) return
    if (callStateRef.current !== 'idle' && callStateRef.current !== 'failed') {
      console.warn('📞 startCall ignored — callState is', callStateRef.current)
      return
    }
    setError('')
    // Clear any stale incoming-call toast — if we're starting a call,
    // we can't also be answering one.
    setIncomingCall(null)
    stopRingtone()
    setIsVideo(video)
    isVideoRef.current = video
    // If this is a voice call, mark video as off so the camera button
    // shows "Ativar câmera" instead of "Desligar câmera".
    setVideoOff(!video)
    videoOffRef.current = !video
    startVideoOffRef.current = !video
    const myId = getCurrentUserId()
    userIdRef.current = myId
    isChannelCallRef.current = false
    setIsChannelCall(false)
    setCallTargetId(targetUserId)
    callTargetIdRef.current = targetUserId
    setCallStateBoth('calling')
    // Generate a call ID for the DM call (no channel).
    const newCallId = 'sfu-dm-' + crypto.randomUUID()
    callIdRef.current = newCallId
    setCallId(newCallId)
    const wsOk = await waitWsOpen()
    if (!wsOk) {
      console.error('📞 SFU startCall: WS not connected after 5s')
      setError('Não foi possível conectar ao servidor de chamadas')
      setCallStateBoth('failed')
      return
    }
    send({
      type: 'sfu-join',
      call_id: newCallId,
      is_video: video,
    })
    // Send call-invite to the target user so they get a ringtone.
    send({
      type: 'call-invite',
      to_user_id: targetUserId,
      call_id: newCallId,
      is_video: video,
      is_group: false,
    })
    persistCall({ callId: newCallId, isChannelCall: false, isVideo: video, targetUserId })
    startRingtone(false)
  }, [startRingtone, stopRingtone, persistCall])

  const startChannelCall = useCallback(async (chId: string, video = false) => {
    if (callStateRef.current !== 'idle' && callStateRef.current !== 'failed') return
    setError('')
    setIsVideo(video)
    isVideoRef.current = video
    setVideoOff(!video)
    videoOffRef.current = !video
    startVideoOffRef.current = !video
    const myId = getCurrentUserId()
    userIdRef.current = myId
    isChannelCallRef.current = true
    setIsChannelCall(true)
    setChannelId(chId)
    channelIdRef.current = chId
    setCallStateBoth('calling')
    // Generate a call ID and tell the server to create the SFU room.
    const newCallId = 'sfu-' + chId + '-' + crypto.randomUUID()
    callIdRef.current = newCallId
    setCallId(newCallId)
    // Wait for WS to be open, then send sfu-join.
    const wsOk = await waitWsOpen()
    if (!wsOk) {
      console.error('📞 SFU startChannelCall: WS not connected after 5s')
      setError('Não foi possível conectar ao servidor de chamadas')
      setCallStateBoth('failed')
      return
    }
    send({
      type: 'sfu-join',
      call_id: newCallId,
      channel_id: chId,
      is_video: video,
    })
    persistCall({ callId: newCallId, isChannelCall: true, isVideo: video, channelId: chId })
  }, [persistCall])

  const joinChannelCall = useCallback(async (callIdArg: string, video = false, chIdArg: string | null = null, forceMuted = false, initialVideoOff: boolean | null = null, isRejoin = false, dmTargetUserId: string | null = null) => {
    if (callStateRef.current !== 'idle' && callStateRef.current !== 'failed') return
    setError('')
    // Clear any other pending incoming-call toast — we're joining now.
    setIncomingCall(null)
    stopRingtone()
    // If force-joined, start with mic and camera off for privacy.
    startMutedRef.current = forceMuted
    // Determine the initial camera-off state. If initialVideoOff is
    // explicitly provided (auto-rejoin), use it to restore the user's
    // exact camera state from before the reload. Otherwise default to
    // !video || forceMuted (camera off for audio calls and forced joins).
    const camOff = initialVideoOff !== null ? initialVideoOff : (!video || forceMuted)
    startVideoOffRef.current = camOff
    setIsVideo(video)
    isVideoRef.current = video
    setVideoOff(camOff)
    videoOffRef.current = camOff
    const myId = getCurrentUserId()
    userIdRef.current = myId
    // A DM call has no channel ID — don't mark it as a channel call.
    isChannelCallRef.current = !!chIdArg
    setIsChannelCall(!!chIdArg)
    if (chIdArg) {
      setChannelId(chIdArg)
      channelIdRef.current = chIdArg
    }
    // 1:1 DM call: remember the other party so DMView can render the
    // embedded call session at the top of the conversation and
    // auto-rejoin can navigate back to the DM after a reload.
    if (dmTargetUserId) {
      setCallTargetId(dmTargetUserId)
      callTargetIdRef.current = dmTargetUserId
    }
    setCallStateBoth('calling')
    callIdRef.current = callIdArg
    setCallId(callIdArg)
    const wsOk = await waitWsOpen()
    if (!wsOk) {
      console.error('📞 SFU joinChannelCall: WS not connected after 5s')
      setError('Não foi possível conectar ao servidor de chamadas')
      setCallStateBoth('failed')
      return
    }
    send({
      type: 'sfu-join',
      call_id: callIdArg,
      channel_id: chIdArg || channelIdRef.current || undefined,
      is_video: video,
      rejoin: isRejoin || undefined,
    })
    persistCall({ callId: callIdArg, isChannelCall: !!chIdArg, isVideo: video, channelId: chIdArg || undefined, forceMuted, videoOff: camOff, targetUserId: dmTargetUserId || undefined })
  }, [persistCall, stopRingtone])
  // Keep the ref in sync so handleSignalingMessage (defined earlier)
  // can call the latest version without capturing a stale closure.
  joinChannelCallRef.current = joinChannelCall

  // ---------- Auto-rejoin on page reload ----------
  // If the page was refreshed while in a call, rejoin automatically.
  // The SFU room persists on the server, so we just need to re-send
  // sfu-join with the same call ID.
  const rejoinAttemptedRef = useRef(false)
  useEffect(() => {
    if (rejoinAttemptedRef.current) return
    rejoinAttemptedRef.current = true
    try {
      const raw = localStorage.getItem(PERSIST_KEY)
      if (!raw) return
      const data = JSON.parse(raw)
      if (!data.callId) return
      console.log('📞 SFU: auto-rejoining call after page reload', data)
      // Navigate back to the call page so the user sees the call
      // instead of being stuck on whatever page they reloaded on.
      try {
        window.dispatchEvent(new CustomEvent('sfu-call-rejoin-navigate', { detail: data }))
      } catch {}
      // Use a small delay to let the WS connect first
      const timer = setTimeout(() => {
        if (callStateRef.current !== 'idle') return
        if (data.isChannelCall) {
          joinChannelCall(data.callId, data.isVideo, data.channelId, data.forceMuted || false, data.videoOff ?? null, true)
        } else {
          // DM call: rejoin the SFU room (no new invite needed)
          const myId = getCurrentUserId()
          userIdRef.current = myId
          isChannelCallRef.current = false
          setIsChannelCall(false)
          setCallTargetId(data.targetUserId)
          callTargetIdRef.current = data.targetUserId
          setCallStateBoth('calling')
          callIdRef.current = data.callId
          setCallId(data.callId)
          setIsVideo(data.isVideo)
          isVideoRef.current = data.isVideo
          // Restore the camera off state from the persisted data so the
          // rejoin matches the user's camera state before the reload.
          const dmCamOff = data.videoOff ?? !data.isVideo
          startVideoOffRef.current = dmCamOff
          setVideoOff(dmCamOff)
          videoOffRef.current = dmCamOff
          waitWsOpen().then(ok => {
            if (!ok) { setCallStateBoth('failed'); clearPersistedCall(); return }
            send({ type: 'sfu-join', call_id: data.callId, is_video: data.isVideo, rejoin: true })
          })
        }
      }, 1500)
      return () => clearTimeout(timer)
    } catch { /* invalid JSON */ }
  }, [joinChannelCall])

  // cleanupCallRef breaks the circular dependency between endCall
  // (defined first) and cleanupCall (defined second).
  const cleanupCallRef = useRef<(() => unknown) | null>(null)

  const endCall = useCallback(() => {
    stopRingtone()
    // Send sfu-leave BEFORE cleanupCall so the WS is still open and
    // callIdRef still has the call ID. send() is synchronous (writes
    // directly to wsRef.current.send), so this completes before
    // cleanupCall runs.
    if (callIdRef.current) {
      console.log('📞 SFU endCall: sending sfu-leave for', callIdRef.current)
      send({ type: 'sfu-leave', call_id: callIdRef.current })
    }
    cleanupCallRef.current?.()
    setCallStateBoth('idle')
  }, [stopRingtone])

  // End the call for ALL participants (channel calls only). Sends
  // sfu-end-call to the server, which removes everyone from the SFU
  // room and notifies all participants to hang up.
  const endCallForEveryone = useCallback(() => {
    if (callIdRef.current) {
      console.log('📞 SFU endCallForEveryone: sending sfu-end-call for', callIdRef.current)
      send({ type: 'sfu-end-call', call_id: callIdRef.current })
    }
    cleanupCallRef.current?.()
    setCallStateBoth('idle')
  }, [])

  // ---------- Local voice activity detection ----------
  // Uses Web Audio API to measure mic input level. When the level
  // crosses a threshold, broadcasts sfu-voice-activity to the SFU
  // which relays it to other participants. Throttled to avoid
  // flooding the WS with state changes.
  // NOTE: Must be defined before cleanupCall, which references
  // stopVoiceDetection in its dependency array.
  const startVoiceDetection = useCallback((stream: MediaStream) => {
    if (voiceDetectRef.current) return // already running
    try {
      const audioTracks = stream.getAudioTracks()
      if (!audioTracks.length) return
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 512
      analyser.smoothingTimeConstant = 0.6
      source.connect(analyser)
      const data = new Uint8Array(analyser.frequencyBinCount)
      let wasSpeaking = false
      let lastSent = 0
      const THRESHOLD = 18 // tuned for typical mic levels
      const loop = () => {
        analyser.getByteFrequencyData(data)
        // Compute average volume across frequency bins
        let sum = 0
        for (let i = 0; i < data.length; i++) sum += data[i]
        const avg = sum / data.length
        const isSpeaking = avg > THRESHOLD && audioTracks[0]?.enabled
        const now = Date.now()
        // Only send state changes, throttled to max 10/s
        if (isSpeaking !== wasSpeaking && now - lastSent > 100) {
          wasSpeaking = isSpeaking
          lastSent = now
          if (callIdRef.current) {
            send({ type: 'sfu-voice-activity', call_id: callIdRef.current, speaking: isSpeaking })
          }
        }
        voiceDetectRef.current!.rafId = requestAnimationFrame(loop)
      }
      voiceDetectRef.current = { ctx, analyser, rafId: 0 }
      voiceDetectRef.current!.rafId = requestAnimationFrame(loop)
    } catch (e: unknown) {
      console.warn('📞 SFU voice detection failed:', e)
    }
  }, [])

  const stopVoiceDetection = useCallback(() => {
    const vd = voiceDetectRef.current
    if (!vd) return
    if (vd.rafId) cancelAnimationFrame(vd.rafId)
    try { vd.ctx.close() } catch {}
    voiceDetectRef.current = null
    // Broadcast that we stopped speaking
    if (callIdRef.current) {
      send({ type: 'sfu-voice-activity', call_id: callIdRef.current, speaking: false })
    }
  }, [])

  const cleanupCall = useCallback(() => {
    stopRingtone()
    if (pcRef.current) {
      try { pcRef.current.close() } catch {}
      pcRef.current = null
    }
    negotiationRef.current = null
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
    }
    setLocalStream(null)
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(t => t.stop())
      screenStreamRef.current = null
    }
    remoteStreamsRef.current = {}
    setRemoteStreams({})
    participantsRef.current = {}
    setParticipants([])
    callIdRef.current = null
    setCallId(null)
    channelIdRef.current = null
    setChannelId(null)
    setCallTargetId(null)
    callTargetIdRef.current = null
    isChannelCallRef.current = false
    setIsChannelCall(false)
    setMuted(false)
    setVideoOff(false)
    setScreenSharing(false)
    setMicError('')
    mutedRef.current = false
    videoOffRef.current = false
    startMutedRef.current = false
    startVideoOffRef.current = false
    screenSharingRef.current = false
    setError('')
    clearPersistedCall()
    stopVoiceDetection()
    setRemoteMuteStates({})
    setRemoteSpeaking({})
    remoteSpeakingRef.current = {}
    setInvitingUsers({})
  }, [stopRingtone, clearPersistedCall, stopVoiceDetection])
  cleanupCallRef.current = cleanupCall

  const toggleMute = useCallback(() => {
    const raw = rawAudioTrackRef.current
    const published = localStreamRef.current?.getAudioTracks()[0]
    const audio = raw || published
    if (!audio) return
    const nextEnabled = !audio.enabled
    audio.enabled = nextEnabled
    if (raw && published && published !== raw) published.enabled = nextEnabled
    mutedRef.current = !nextEnabled
    setMuted(!nextEnabled)
    // Broadcast mute state to other participants
    if (callIdRef.current) {
      send({ type: 'sfu-mute-state', call_id: callIdRef.current, muted: !nextEnabled })
    }
  }, [])

  // Mute another participant remotely
  const muteRemote = useCallback((userId: string) => {
    if (!callIdRef.current || !userId) return
    send({ type: 'sfu-remote-mute', call_id: callIdRef.current, to_user_id: userId })
  }, [])

  // Unmute another participant remotely
  const unmuteRemote = useCallback((userId: string) => {
    if (!callIdRef.current || !userId) return
    send({ type: 'sfu-remote-unmute', call_id: callIdRef.current, to_user_id: userId })
  }, [])

  const toggleVideo = useCallback(async () => {
    const stream = localStreamRef.current
    if (!stream) return
    const videoTrack = stream.getVideoTracks()[0]
    // If we already have a video track, just toggle it.
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled
      videoOffRef.current = !videoTrack.enabled
      setVideoOff(!videoTrack.enabled)
      // Persist the camera on/off state so auto-rejoin after a reload
      // restores the camera exactly as the user left it.
      updatePersistedCall({ isVideo: true, videoOff: !videoTrack.enabled })
      return
    }
    // No video track yet — the call started as voice-only or the camera
    // was unavailable. Try to acquire the camera now and add it to the
    // PC with a renegotiation offer.
    const tryGetCamera = async (constraints: MediaStreamConstraints) => {
      // On Windows, a failed getUserMedia can leave the camera locked
      // for a brief moment. Retry up to 3 times with increasing delay.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await navigator.mediaDevices.getUserMedia(constraints)
        } catch (err: unknown) {
          if ((err as Error).name === 'NotReadableError' && attempt < 2) {
            console.warn(`📞 SFU toggleVideo: camera busy (attempt ${attempt + 1}/3), retrying in ${500 * (attempt + 1)}ms…`)
            await new Promise(r => setTimeout(r, 500 * (attempt + 1)))
            continue
          }
          throw err
        }
      }
    }
    try {
      const dev = selectedDevicesRef.current
      // Try with specific device first, then fall back to relaxed
      // constraints (no deviceId, lower resolution) which works better
      // on Windows with certain camera drivers.
      let videoStream
      try {
        videoStream = await tryGetCamera({
          audio: false,
          video: dev.videoIn ? { deviceId: { exact: dev.videoIn } } : true,
        })
      } catch (err1: unknown) {
        if ((err1 as Error).name === 'NotReadableError' || (err1 as Error).name === 'OverconstrainedError') {
          // Retry with relaxed constraints — no specific device, just
          // ask for any camera at a modest resolution. Some Windows
          // drivers fail with unconstrained video: true but work with
          // explicit width/height.
          console.warn('📞 SFU toggleVideo: retrying with relaxed constraints')
          videoStream = await tryGetCamera({
            audio: false,
            video: { width: { ideal: 640 }, height: { ideal: 480 } },
          })
        } else {
          throw err1
        }
      }
      const newTrack = videoStream!.getVideoTracks()[0]
      if (!newTrack) return
      // Add the track to the existing local stream so toggleMute and
      // future toggleVideo calls find it.
      stream.addTrack(newTrack)
      // Refresh the local preview.
      setLocalStream(new MediaStream(stream.getTracks()))
      const pc = pcRef.current
      if (pc) {
        // Use addTransceiver with sendonly instead of addTrack —
        // addTrack reuses an existing transceiver (e.g. a recvonly
        // video transceiver the SFU added for forwarding another
        // participant's video), causing an m-line collision where the
        // SFU can't receive our video on the same m-line it's using
        // to send. addTransceiver forces a NEW m-line dedicated to
        // our outgoing video.
        pc.addTransceiver(newTrack, { direction: 'sendonly' })
        await negotiationRef.current?.offer()
      }
      isVideoRef.current = true
      setIsVideo(true)
      videoOffRef.current = false
      setVideoOff(false)
      setMicError('')
      // Persist the new video state so auto-rejoin after a reload
      // rejoins as a video call with the camera on.
      updatePersistedCall({ isVideo: true, videoOff: false })
    } catch (err: unknown) {
      console.error('📞 SFU toggleVideo: failed to acquire camera:', (err as Error).message)
      const reason = (err as Error).name === 'NotAllowedError'
        ? 'Permissão de câmera negada. Verifique em chrome://settings/content/camera'
        : (err as Error).name === 'NotReadableError'
        ? 'Câmera indisponível. Verifique se outro app (Zoom, Teams, Windows Hello) não está usando a câmera, ou reinicie o Chrome.'
        : (err as Error).name === 'NotFoundError'
        ? 'Nenhuma câmera encontrada.'
        : `Câmera indisponível: ${(err as Error).message}`
      setMicError(reason)
    }
  }, [updatePersistedCall])

  const toggleScreenShare = useCallback(async () => {
    if (screenSharingRef.current) {
      // Stop screen share: removeTrack the screen sender and renegotiate
      // so the SFU detects the track end and cleans up other participants.
      const pc = pcRef.current
      if (pc && screenShareSenderRef.current) {
        try {
          pc.removeTrack(screenShareSenderRef.current)
          await negotiationRef.current?.offer()
        } catch (err: unknown) {
          console.error('📞 SFU stop screen share failed:', err)
        }
        screenShareSenderRef.current = null
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop())
        screenStreamRef.current = null
      }
      // Point the local preview back at the camera/mic stream.
      setLocalStream(localStreamRef.current)
      screenSharingRef.current = false
      setScreenSharing(false)
      return
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: false,
      })
      screenStreamRef.current = display
      // Show the screen in the local preview. We only swap the preview
      // surface — localStreamRef still holds the camera/mic stream so
      // toggleMute and toggleVideo keep controlling the real devices.
      setLocalStream(new MediaStream([display.getVideoTracks()[0]]))
      const pc = pcRef.current
      const screenTrack = display.getVideoTracks()[0]
      try { screenTrack.contentHint = 'detail' } catch {}
      // Always add screen share as a NEW track on its own m-line via
      // addTransceiver. This triggers OnTrack on the SFU, which creates
      // a PublishedTrack and forwards it to all other participants.
      // replaceTrack on the camera sender does NOT trigger OnTrack, so
      // the SFU never learns about the screen and nobody sees it.
      if (pc) {
        const transceiver = pc.addTransceiver(screenTrack, { direction: 'sendonly' })
        screenShareSenderRef.current = transceiver.sender
        await negotiationRef.current?.offer()
      }
      display.getVideoTracks()[0].onended = () => {
        // User clicked browser's "Stop sharing" button.
        toggleScreenShare()
      }
      screenSharingRef.current = true
      setScreenSharing(true)
    } catch (err: unknown) {
      console.error('📞 SFU screen share failed:', err)
    }
  }, [])

  const acceptCall = useCallback(() => {
    // SFU calls don't use the incoming-call flow the same way P2P does.
    // For now, acceptCall just joins the channel call if there is one.
    if (!incomingCall?.callId) return
    stopRingtone()
    // 1:1 DM invite has no channel_id — the "target" is the caller.
    const dmTargetId = incomingCall.channelId ? null : (incomingCall.fromUserId || null)
    joinChannelCall(incomingCall.callId, incomingCall.isVideo, incomingCall.channelId, false, null, false, dmTargetId)
    setIncomingCall(null)
    // Take the answerer to the call screen — the DM with the caller, or
    // the channel — so the call session at the top of the conversation is
    // visible. Navigation is a push (not replace) so the back button still
    // returns to wherever the user was, and they can leave/re-enter the
    // conversation freely while the call stays alive.
    try {
      window.dispatchEvent(new CustomEvent('sfu-call-navigate', {
        detail: {
          isChannelCall: !!incomingCall.channelId,
          channelId: incomingCall.channelId,
          targetUserId: dmTargetId,
          push: true,
        },
      }))
    } catch {}
  }, [incomingCall, joinChannelCall, stopRingtone])

  const rejectCall = useCallback(() => {
    stopRingtone()
    setIncomingCall(null)
  }, [stopRingtone])

  // Invite a user to the current call (rings them — they can accept or
  // reject). Sends call-invite via the existing P2P signaling path so
  // the target hears a ringtone even though the media itself goes
  // through the SFU.
  const inviteToCall = useCallback((targetUserId: string) => {
    if (!callIdRef.current) return
    // Mark user as "calling" so the UI shows a calling indicator
    setInvitingUsers(prev => ({ ...prev, [targetUserId]: 'calling' }))
    send({
      type: 'call-invite',
      to_user_id: targetUserId,
      call_id: callIdRef.current,
      is_video: isVideoRef.current,
      is_group: true,
      channel_id: channelIdRef.current || '',
    })
    // Auto-timeout after 30s: mark as 'timeout' so user can re-invite
    setTimeout(() => {
      setInvitingUsers(prev => {
        if (prev[targetUserId] === 'calling') {
          return { ...prev, [targetUserId]: 'timeout' }
        }
        return prev
      })
    }, 30000)
  }, [])

  // inviteToCallSilent: sends a DM to the target user with a call
  // invite button. The DM has message_type='call_invite' and the
  // content is JSON with call_id, channel_id, is_video, and inviter
  // name. DMView renders this as a special message with an "Entrar
  // na chamada" button.
  const inviteToCallSilent = useCallback(async (targetUserId: string) => {
    if (!callIdRef.current) return
    const token = getToken()
    if (!token) return
    // Get the inviter's name so the message is personalized.
    let inviterName = 'Alguém'
    try {
      const resp = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      const data = await resp.json()
      const me = (data.users || []).find((u: PublicUser) => u.id === userIdRef.current)
      if (me) inviterName = me.display_name || me.name || me.email || 'Alguém'
    } catch {}
    const content = JSON.stringify({
      call_id: callIdRef.current,
      channel_id: channelIdRef.current || '',
      is_video: isVideoRef.current,
      inviter_name: inviterName,
    })
    try {
      await fetch(`${API_URL}/api/dm/${targetUserId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content, message_type: 'call_invite' }),
      })
    } catch (err: unknown) {
      console.error('📞 SFU inviteToCallSilent failed:', err)
    }
  }, [])

  // Force a user to join the current call. Unlike inviteToCall (which
  // rings and waits for acceptance), this immediately makes the target
  // client join the SFU room. Useful for "pulling" someone into an
  // ongoing call without waiting for them to answer.
  const forceJoinCall = useCallback((targetUserId: string) => {
    if (!callIdRef.current) return
    send({
      type: 'sfu-force-join',
      to_user_id: targetUserId,
      call_id: callIdRef.current,
      is_video: isVideoRef.current,
      channel_id: channelIdRef.current || '',
    })
  }, [])

  // Remove a participant from the current call. The server will
  // remove them from the SFU room and send sfu-call-ended to their
  // client so their UI tears down.
  const removeFromCall = useCallback((targetUserId: string) => {
    if (!callIdRef.current) return
    send({
      type: 'sfu-kick',
      to_user_id: targetUserId,
      call_id: callIdRef.current,
    })
  }, [])

  // ---------- Device selection ----------
  // loadDevices enumerates media devices and populates the `devices`
  // state so the settings UI can render <option> lists. Saved ids that
  // no longer exist (unplugged headset) fall back to the OS default.
  const loadDevices = useCallback(async () => {
    try {
      let allDevices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices()
      const labelsHidden = allDevices.length > 0 && allDevices.every(d => !d.label)
      const noAudioListed = !allDevices.some(d => d.kind === 'audioinput' || d.kind === 'audiooutput')
      // Chrome only fills device labels / audio lists after a successful
      // getUserMedia. If permission is now Allow but we never captured
      // audio (mic was Block, or only the camera opened), unlock here
      // with a throwaway stream so the settings dropdowns populate.
      if (labelsHidden || noAudioListed) {
        // Try unlock with getUserMedia up to 3 times with increasing
        // delays. Right after a device is unplugged, Chrome's audio
        // session may still be releasing the old device, and the first
        // getUserMedia can fail with NotFoundError even though the
        // built-in mic exists. Waiting 500ms-1s usually fixes it.
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const unlock = await navigator.mediaDevices.getUserMedia({
              audio: unconstrainedAudioConstraints(),
              video: false,
            }).catch(() => navigator.mediaDevices.getUserMedia({ audio: true, video: false }))
            unlock.getTracks().forEach(t => t.stop())
            console.log(`📞 SFU loadDevices: unlock succeeded on attempt ${attempt}`)
            break
          } catch (err: unknown) {
            console.warn(`📞 SFU loadDevices: unlock attempt ${attempt} failed`, (err as Error).name, (err as Error).message)
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, attempt * 500))
            }
          }
        }
        // Camera-only unlock as last resort — at least fills video
        // labels and may trigger Chrome to re-enumerate audio too.
        try {
          const cam = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
          cam.getTracks().forEach(t => t.stop())
        } catch {}
        allDevices = await navigator.mediaDevices.enumerateDevices()
        console.log('📞 SFU loadDevices: after unlock, devices', allDevices.map(d => ({ kind: d.kind, label: d.label?.slice(0, 20), deviceId: d.deviceId?.slice(0, 8) })))
      }
      const audioIn = allDevices.filter(d => d.kind === 'audioinput')
      const audioOut = allDevices.filter(d => d.kind === 'audiooutput')
      const videoIn = allDevices.filter(d => d.kind === 'videoinput')
      setDevices({ audioIn, audioOut, videoIn })
      let saved: Record<string, string> = {}
      try { saved = JSON.parse(localStorage.getItem('atlas_selected_devices') || '{}') } catch {}
      const prev = selectedDevicesRef.current
      const next = {
        audioIn: normalizeDeviceId(prev.audioIn || saved.audioIn, audioIn),
        // Prefer the live OS default when the saved speaker vanished
        // (unplugged headset). Forcing a dead sinkId mutes remote audio.
        audioOut: audioOut.some(d => d.deviceId === (prev.audioOut || saved.audioOut))
          ? (prev.audioOut || saved.audioOut)
          : (audioOut.find(d => d.deviceId === 'default')?.deviceId || ''),
        videoIn: normalizeDeviceId(prev.videoIn || saved.videoIn, videoIn),
      }
      const changed = next.audioIn !== prev.audioIn
        || next.audioOut !== prev.audioOut
        || next.videoIn !== prev.videoIn
      if (changed) {
        selectedDevicesRef.current = next
        setSelectedDevices(next)
      }
      // Clear dead device IDs from localStorage so a page reload
      // doesn't try to use a device that was unplugged.
      const savedAlive = {
        audioIn: audioIn.some(d => d.deviceId === next.audioIn) ? next.audioIn : '',
        audioOut: audioOut.some(d => d.deviceId === next.audioOut) ? next.audioOut : '',
        videoIn: videoIn.some(d => d.deviceId === next.videoIn) ? next.videoIn : '',
      }
      try {
        if (savedAlive.audioIn || savedAlive.audioOut || savedAlive.videoIn) {
          localStorage.setItem('atlas_selected_devices', JSON.stringify(savedAlive))
        } else {
          localStorage.removeItem('atlas_selected_devices')
        }
      } catch {}
    } catch {}
  }, [])

  // changeDevice updates the selected device for a given kind and, if
  // we're in an active call, replaces the track on the existing
  // PeerConnection so the new device takes effect immediately without
  // dropping the call.
  const recaptureLocalMedia = useCallback(async () => {
    if (callStateRef.current !== 'connected' && callStateRef.current !== 'calling') return
    // Guard against concurrent calls — devicechange fires multiple
    // times when a headset (mic+speaker) is unplugged, and each one
    // would race to set localStreamRef and stop oldStream.
    if (recapturingRef.current) {
      console.log('📞 SFU recaptureLocalMedia: already running, skipping')
      return
    }
    recapturingRef.current = true
    const video = isVideoRef.current
    const oldStream = localStreamRef.current
    localStreamRef.current = null
    try {
      let stream
      try {
        stream = await getLocalStream(video)
      } catch (firstErr: unknown) {
        // getLocalStream's getAnyAudioStream already tried 5
        // strategies. If we're here, every deviceId-aware attempt
        // failed. Try a completely bare getUserMedia with zero
        // constraints — no deviceId, no echoCancellation, nothing.
        // Chrome sometimes fails all constrained requests right
        // after a device removal but accepts a bare audio:true.
        console.warn('📞 SFU recaptureLocalMedia: getLocalStream failed, trying bare getUserMedia', (firstErr as Error).name)
        try {
          const bare = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
          if (video) {
            try {
              const cam = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
              cam.getVideoTracks().forEach(t => bare.addTrack(t))
            } catch {}
          }
          stream = bare
        } catch (bareErr: unknown) {
          throw firstErr
        }
      }
      localStreamRef.current = stream
      const pc = pcRef.current
      if (pc) {
        const oldIds = new Set((oldStream?.getTracks() || []).map(t => t.id))
        stream.getTracks().forEach((newTrack) => {
          const sender = pc.getSenders().find(s => s.track && (
            oldIds.has(s.track.id) || s.track.id === newTrack.id
          ) && s.track.kind === newTrack.kind)
          if (sender) sender.replaceTrack(newTrack)
        })
      }
      if (localVideoRef.current) localVideoRef.current.srcObject = stream
      if (oldStream) oldStream.getTracks().forEach(t => t.stop())
      setMicError('')
    } catch (err: unknown) {
      // Don't restore the old stream — its tracks are from the
      // removed device and are ended/dead. Keeping a dead stream
      // makes the PC send silence and the user thinks "everything
      // broke". Instead, keep localStreamRef null so the next
      // devicechange or manual retry can try again.
      console.error('📞 SFU recaptureLocalMedia failed:', err)
      if (oldStream) {
        try { oldStream.getTracks().forEach(t => t.stop()) } catch {}
      }
      localStreamRef.current = null
      setLocalStream(null)
      const perm = await getMicPermissionState()
      setMicError(audioErrorMessage(err as Error, perm))
    } finally {
      recapturingRef.current = false
    }
  }, [getLocalStream])

  const changeDevice = useCallback(async (type: 'audioIn' | 'audioOut' | 'videoIn', deviceId: string) => {
    const newDevices = { ...selectedDevicesRef.current, [type]: deviceId }
    selectedDevicesRef.current = newDevices
    setSelectedDevices(newDevices)
    if (callStateRef.current !== 'connected' && callStateRef.current !== 'calling') return
    // Speaker is applied via setSinkId on the <audio> elements — no
    // recapture needed. Recapturing here used to restart the mic for
    // no reason and often landed on the notebook again.
    if (type === 'audioOut') return
    await recaptureLocalMedia()
  }, [recaptureLocalMedia])
  recaptureLocalMediaRef.current = recaptureLocalMedia

  // changeInputVolume / changeOutputVolume — simple state setters.
  // CallView applies outputVolume to <audio> elements; inputVolume is
  // applied via the pipeline GainNode when the graph is live.
  const changeInputVolume = useCallback((vol: number) => {
    inputVolumeRef.current = vol
    setInputVolume(vol)
    // Volume is applied at the destination <audio> via outputVolume.
    // We do not insert a GainNode on the send path — that would break AEC.
  }, [])

  const changeOutputVolume = useCallback((vol: number) => {
    setOutputVolume(vol)
  }, [])

  // Persist device selection when it changes so it survives refresh.
  useEffect(() => {
    selectedDevicesRef.current = selectedDevices
    if (selectedDevices.audioIn || selectedDevices.audioOut || selectedDevices.videoIn) {
      localStorage.setItem('atlas_selected_devices', JSON.stringify(selectedDevices))
    }
  }, [selectedDevices])

  // ---------- Lifecycle ----------
  useEffect(() => {
    connectWS()
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        send({ type: 'ping' })
      } else if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        connectWS()
      }
      // If CONNECTING or CLOSING, do nothing — let it finish
    }, 10000)
    // Persist the current camera/mic state right before the page unloads
    // so auto-rejoin after a reload restores the exact state the user had
    // (camera on/off, muted) instead of the stale initial state from when
    // the call was first joined.
    const handleBeforeUnload = () => {
      if (callStateRef.current === 'connected' || callStateRef.current === 'calling') {
        updatePersistedCall({
          isVideo: isVideoRef.current,
          videoOff: videoOffRef.current,
          muted: mutedRef.current,
        })
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handleBeforeUnload)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) connectWS()
        if (audioContextRef.current?.state === 'suspended') {
          audioContextRef.current.resume().catch(() => {})
        }
      }
    }
    const handleOnline = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) connectWS()
    }
    // Listen for sfu-forced-join events from the notification WS (when
    // the call WS is not connected). This ensures force-join works even
    // if the user just opened the page and the call WS hasn't connected
    // yet, or if the call WS dropped temporarily.
    const handleForcedJoinNotif = (e: Event) => {
      const msg = (e as CustomEvent).detail
      console.log('📞 SFU forced join (notif WS) by', msg.from_user_id, 'call=', msg.call_id)
      if (callStateRef.current === 'idle' || callStateRef.current === 'failed') {
        // Ensure call WS is connected before joining.
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          connectWS()
          // Wait for WS to connect, then join.
          waitWsOpen().then(ok => {
            if (ok) {
              joinChannelCallRef.current?.(msg.call_id ?? '', msg.is_video ?? false, msg.channel_id, true)
            } else {
              console.error('📞 SFU forced-join: WS not connected after 5s')
            }
          })
        } else {
          joinChannelCallRef.current?.(msg.call_id ?? '', msg.is_video ?? false, msg.channel_id, true)
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('online', handleOnline)
    window.addEventListener('sfu-forced-join', handleForcedJoinNotif)
    // Listen for channel-call-invite via the notification WS (when the
    // call WS is not connected yet). Starts the ringtone immediately so
    // the user hears the call even before the call WS delivers the
    // invite message.
    const handleChannelInviteNotif = (e: Event) => {
      const msg = (e as CustomEvent).detail
      if (!msg?.from_user_id || !msg?.call_id) return
      if (callStateRef.current !== 'idle' && callStateRef.current !== 'failed') return
      if (incomingCallRef.current?.callId === msg.call_id) return
      startRingtone(true)
      // Set incomingCall so the UI shows the incoming-call toast.
      setIncomingCall({
        fromUserId: msg.from_user_id,
        callId: msg.call_id,
        sdp: null,
        isVideo: msg.is_video,
        isGroup: msg.is_group !== false,
        isChannelInvite: true,
        channelId: msg.channel_id || null,
        callerName: msg.sender_name,
        callerAvatar: msg.sender_avatar,
        forced: false,
      })
      // Safety: stop the ringtone after 30s if the user doesn't answer.
      // The toast itself stays visible until accept/reject or a
      // call-ended signal — only the sound stops.
      if (ringtoneTimeoutRef.current) clearTimeout(ringtoneTimeoutRef.current)
      ringtoneTimeoutRef.current = setTimeout(() => {
        if (incomingCallRef.current?.callId === msg.call_id) stopRingtone()
      }, 30000)
    }
    // Listen for call-ended / call-rejected via notification WS so we
    // stop the ringtone even if the call WS is disconnected.
    const handleCallEndedNotif = (e: Event) => {
      stopRingtone()
      const notifCallId = (e as CustomEvent).detail?.call_id
      if (!notifCallId || notifCallId === callIdRef.current) {
        const st = callStateRef.current
        if (st === 'calling' || (st === 'connected' && e.type === 'call-ended-notif')) {
          if (st === 'connected') {
            setError('A outra pessoa encerrou a chamada')
          }
          cleanupCallRef.current?.()
          setCallStateBoth(st === 'connected' ? 'failed' : 'idle')
        }
      }
      setIncomingCall(null)
    }
    window.addEventListener('channel-call-invite', handleChannelInviteNotif)
    window.addEventListener('call-ended-notif', handleCallEndedNotif)
    window.addEventListener('call-rejected-notif', handleCallEndedNotif)
    // Re-enumerate when devices are plugged/unplugged (e.g. headset).
    // If the user is on the OS default (or their saved device vanished),
    // recapture so the live track follows the new default instead of
    // staying stuck on the notebook mic.
    const handleDeviceChange = async () => {
      // Debounce: devicechange fires in bursts when a headset with
      // mic+speaker is unplugged (one event per device). Wait 800ms
      // for all events to settle before running loadDevices +
      // recapture, otherwise we race and recapture multiple times.
      if (deviceChangeTimerRef.current) clearTimeout(deviceChangeTimerRef.current)
      deviceChangeTimerRef.current = setTimeout(async () => {
        deviceChangeTimerRef.current = null
        // Snapshot the device list BEFORE doing anything. If nothing
        // actually changed (spurious devicechange from navigating,
        // clicking, other tabs acquiring/releasing devices, etc.),
        // skip the stop+recapture entirely — otherwise we kill the
        // mic/camera on every spurious event.
        let beforeDeviceIds: string[] = []
        try {
          beforeDeviceIds = (await navigator.mediaDevices.enumerateDevices())
            .map(d => `${d.kind}:${d.deviceId}`)
            .sort()
        } catch {}
        const before = { ...selectedDevicesRef.current }
        await loadDevices()
        const after = selectedDevicesRef.current
        // Re-enumerate after loadDevices and compare. If the device
        // list is identical, this was a spurious devicechange.
        let afterDeviceIds: string[] = []
        try {
          afterDeviceIds = (await navigator.mediaDevices.enumerateDevices())
            .map(d => `${d.kind}:${d.deviceId}`)
            .sort()
        } catch {}
        const devicesChanged = beforeDeviceIds.join(',') !== afterDeviceIds.join(',')
        const inputChanged = after.audioIn !== before.audioIn
        console.log('📞 SFU handleDeviceChange: devices after loadDevices', {
          before, after, devicesChanged, inputChanged,
          beforeCount: beforeDeviceIds.length, afterCount: afterDeviceIds.length,
        })
        const inCall = callStateRef.current === 'connected' || callStateRef.current === 'calling'
        if (!inCall) return
        // Only stop+recapture if devices actually changed or the
        // selected input device changed. The old `followsDefault`
        // check caused a recapture on EVERY devicechange event when
        // the user was on the OS default (the common case), killing
        // the mic/camera even when nothing changed.
        if (!devicesChanged && !inputChanged) return
        // CRITICAL: Stop the old stream's tracks BEFORE recapture.
        // When a headset is unplugged, its tracks are 'ended' but not
        // 'stopped'. Chrome keeps the audio session open, which blocks
        // new getUserMedia calls. Stopping the tracks releases the
        // session so Chrome can acquire the built-in mic.
        const oldStream = localStreamRef.current
        if (oldStream) {
          console.log('📞 SFU handleDeviceChange: stopping old stream tracks before recapture')
          try { oldStream.getTracks().forEach(t => t.stop()) } catch {}
        }
        recaptureLocalMediaRef.current?.()
      }, 800)
    }
    navigator.mediaDevices?.addEventListener?.('devicechange', handleDeviceChange)
    return () => {
      clearInterval(pingInterval)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('sfu-forced-join', handleForcedJoinNotif)
      window.removeEventListener('channel-call-invite', handleChannelInviteNotif)
      window.removeEventListener('call-ended-notif', handleCallEndedNotif)
      window.removeEventListener('call-rejected-notif', handleCallEndedNotif)
      navigator.mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange)
      if (deviceChangeTimerRef.current) {
        clearTimeout(deviceChangeTimerRef.current)
        deviceChangeTimerRef.current = null
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      // Close the Call WS so a remount (React StrictMode / HMR) does
      // not leave an orphan socket registered under this user. The
      // orphan used to steal the server-side callClients slot and the
      // SFU answer never reached the live tab.
      if (wsRef.current) {
        try { wsRef.current.onclose = null; wsRef.current.close() } catch {}
        wsRef.current = null
      }
      if (pcRef.current) { try { pcRef.current.close() } catch {} }
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop())
      if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach(t => t.stop())
      stopRingtone()
    }
  }, [connectWS, startRingtone, stopRingtone, updatePersistedCall])

  // playSoundToAll: plays an audio file to all participants via the SFU
  // AND locally so the sender hears it too. Uses replaceTrack to swap the
  // sender's audio track with a mix of mic + sound, then restores when done.
  const soundMixCtxRef = useRef<AudioContext | null>(null)
  const playSoundToAll = useCallback(async (soundUrl: string) => {
    const pc = pcRef.current
    const localStream = localStreamRef.current
    if (!pc || !localStream) return
    const micTrack = localStream.getAudioTracks()[0]
    if (!micTrack) return
    const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio')
    if (!sender) return
    // If a previous sound is still playing, stop it first.
    if (soundMixCtxRef.current) {
      try { soundMixCtxRef.current.close() } catch {}
      soundMixCtxRef.current = null
    }
    // Snapshot the current sender track BEFORE replacing — this is
    // what we restore when the sound ends. Must be the actual track
    // object the sender is using right now.
    const originalTrack = sender.track
    if (!originalTrack) return
    try {
      // Fetch and decode the audio file.
      const resp = await fetch(soundUrl)
      const buf = await resp.arrayBuffer()
      const ctx = new AudioContext()
      soundMixCtxRef.current = ctx
      if (ctx.state === 'suspended') await ctx.resume().catch(() => {})
      const audioBuffer = await ctx.decodeAudioData(buf)
      // Mic source (so the user can still talk over the sound).
      const micSource = ctx.createMediaStreamSource(new MediaStream([micTrack]))
      // Sound source.
      const soundSource = ctx.createBufferSource()
      soundSource.buffer = audioBuffer
      // Mix mic + sound into a single destination for the SFU sender.
      const dest = ctx.createMediaStreamDestination()
      micSource.connect(dest)
      soundSource.connect(dest)
      // ALSO connect the sound to ctx.destination so the local user
      // hears it through their own speakers.
      soundSource.connect(ctx.destination)
      // Replace the sender's track with the mixed track.
      const mixedTrack = dest.stream.getAudioTracks()[0]
      await sender.replaceTrack(mixedTrack)
      soundSource.start()
      let restored = false
      const restore = async () => {
        if (restored) return
        restored = true
        // Restore the original mic track.
        try { await sender.replaceTrack(originalTrack) } catch (e: unknown) {
          console.error('📞 SFU playSoundToAll: restore failed', e)
        }
        try { ctx.close() } catch {}
        soundMixCtxRef.current = null
      }
      soundSource.onended = restore
      // Safety: restore after the buffer duration + 1s in case
      // onended doesn't fire.
      setTimeout(restore, (audioBuffer.duration + 1) * 1000)
    } catch (err: unknown) {
      console.error('📞 SFU playSoundToAll failed:', err)
      // Try to restore the original track on error.
      try { await sender.replaceTrack(originalTrack) } catch {}
      try { soundMixCtxRef.current?.close() } catch {}
      soundMixCtxRef.current = null
    }
  }, [])

  return {
    callState,
    callId,
    channelId,
    isChannelCall,
    isVideo,
    muted,
    videoOff,
    screenSharing,
    error,
    micError,
    reconnecting,
    remoteStreams,
    participants,
    callTargetId,
    incomingCall,
    remoteMuteStates,
    remoteSpeaking,
    invitingUsers,
    selectedDevices,
    devices,
    inputVolume,
    outputVolume,
    localVideoRef,
    setSelectedDevices,
    setOutputVolume,
    startCall,
    startChannelCall,
    joinChannelCall,
    endCall,
    endCallForEveryone,
    toggleMute,
    muteRemote,
    unmuteRemote,
    toggleVideo,
    toggleScreenShare,
    acceptCall,
    rejectCall,
    loadDevices,
    inviteToCall,
    inviteToCallSilent,
    forceJoinCall,
    removeFromCall,
    changeDevice,
    changeInputVolume,
    changeOutputVolume,
    playSoundToAll,
    localStream,
  }
}
